import { Dtos } from "@inrev/common";
import { useFetchAccountPreviews, useFetchBondPreviews } from "@portal/api/previews";
import {
	formatMetricCardCurrency,
	TrendMetricCard,
	type TrendMetricCardProps,
} from "@portal/components/patterns/metric-card";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useMemo } from "react";
import type { EntityScope } from "../../../shared/scope";
import { computeWrittenAccountMetrics } from "./computeWrittenAccountMetrics";

const EMPTY_ACCOUNT_PREVIEWS: Dtos.SuretyAccount.Preview.Get.Response[] = [];
const EMPTY_BOND_PREVIEWS: Dtos.SuretyBond.Preview.Get.Response[] = [];

type WrittenAccountMetricsViewProps = {
	scope: EntityScope;
};

export const WrittenAccountMetricsView = ({ scope }: WrittenAccountMetricsViewProps) => {
	const { user } = useAuthenticatedUser();
	const { accountPreviews } = useFetchAccountPreviews();
	const { bondPreviews } = useFetchBondPreviews();

	const metrics = useMemo(
		() =>
			computeWrittenAccountMetrics({
				accountPreviews: accountPreviews ?? EMPTY_ACCOUNT_PREVIEWS,
				bondPreviews: bondPreviews ?? EMPTY_BOND_PREVIEWS,
				scope,
				currentUserId: user.id,
			}),
		[accountPreviews, bondPreviews, scope, user.id],
	);

	const avgOpenExposureCurrency = formatMetricCardCurrency(metrics.avgOpenExposure);
	const ttmGwpCurrency = formatMetricCardCurrency(metrics.ttmGwp.current);
	const totalOpenExposureCurrency = formatMetricCardCurrency(metrics.totalOpenExposure);

	const cards: TrendMetricCardProps[] = [
		{
			testId: "metric-total-written",
			label: "Total",
			value: metrics.total,
			tone: "accent",
		},
		{
			testId: "metric-active-written",
			label: "Active",
			value: metrics.active,
			tone: "accent",
		},
		{
			testId: "metric-avg-open-exposure",
			label: "Avg Open Exposure",
			value: avgOpenExposureCurrency.display,
			valueTooltip: avgOpenExposureCurrency.full,
			tone: "confirm",
		},
		{
			testId: "metric-ttm-gwp",
			label: "TTM GWP",
			value: ttmGwpCurrency.display,
			valueTooltip: ttmGwpCurrency.full,
			series: metrics.ttmGwp.series,
			current: metrics.ttmGwp.current,
			prior: metrics.ttmGwp.prior,
			deltaFormat: "percent",
			tone: "confirm",
		},
		{
			testId: "metric-total-open-exposure",
			label: "Total Open Exposure",
			value: totalOpenExposureCurrency.display,
			valueTooltip: totalOpenExposureCurrency.full,
			series: metrics.openExposureTrend.series,
			current: metrics.openExposureTrend.current,
			prior: metrics.openExposureTrend.prior,
			deltaFormat: "percent",
			tone: "confirm",
			caption: "last 30d",
		},
	];

	return (
		<div className="grid max-w-8xl grid-cols-3 gap-4 lg:grid-cols-5">
			{cards.map((card) => (
				<TrendMetricCard key={card.testId} {...card} />
			))}
		</div>
	);
};
